import React, { useRef, useState } from 'react';
import {
  PlusIcon,
  XIcon,
  FileTextIcon,
  ImageIcon,
  FileSpreadsheetIcon,
  FileCodeIcon,
  AlertCircleIcon
} from 'lucide-react';

// Size limits based on token budget:
// - Code/text files: 50KB ≈ 12,500 tokens (leaves room for prompt + response)
// - Config files: 100KB (can be verbose but still manageable)
// - Images: 5MB (compressed by APIs, use detail:'low' for GPT)
// - PDFs: 10MB (handled natively by Claude/Gemini)
const CODE_FILE_MAX = 50 * 1024;      // 50KB for code files
const CONFIG_FILE_MAX = 100 * 1024;   // 100KB for config files
const TEXT_FILE_MAX = 50 * 1024;      // 50KB for text/csv

// Supported file types and their configurations
const SUPPORTED_FILES = {
  // Images - all APIs support these (detail:'low' keeps tokens reasonable)
  'image/jpeg': { icon: ImageIcon, label: 'JPEG', maxSize: 5 * 1024 * 1024 },
  'image/jpg': { icon: ImageIcon, label: 'JPG', maxSize: 5 * 1024 * 1024 },
  'image/png': { icon: ImageIcon, label: 'PNG', maxSize: 5 * 1024 * 1024 },
  'image/gif': { icon: ImageIcon, label: 'GIF', maxSize: 5 * 1024 * 1024 },
  'image/webp': { icon: ImageIcon, label: 'WebP', maxSize: 5 * 1024 * 1024 },

  // PDF - Claude and Gemini support natively
  'application/pdf': { icon: FileTextIcon, label: 'PDF', maxSize: 10 * 1024 * 1024 },

  // Text/Data files
  'text/csv': { icon: FileSpreadsheetIcon, label: 'CSV', maxSize: TEXT_FILE_MAX },
  'text/plain': { icon: FileTextIcon, label: 'TXT', maxSize: TEXT_FILE_MAX },

  // Code files - JavaScript/TypeScript
  'text/javascript': { icon: FileCodeIcon, label: 'JS', maxSize: CODE_FILE_MAX },
  'application/javascript': { icon: FileCodeIcon, label: 'JS', maxSize: CODE_FILE_MAX },
  'text/typescript': { icon: FileCodeIcon, label: 'TS', maxSize: CODE_FILE_MAX },
  'application/typescript': { icon: FileCodeIcon, label: 'TS', maxSize: CODE_FILE_MAX },

  // Code files - Web
  'text/html': { icon: FileCodeIcon, label: 'HTML', maxSize: CODE_FILE_MAX },
  'text/css': { icon: FileCodeIcon, label: 'CSS', maxSize: CODE_FILE_MAX },

  // Code files - Other languages
  'text/x-python': { icon: FileCodeIcon, label: 'PY', maxSize: CODE_FILE_MAX },
  'application/x-python': { icon: FileCodeIcon, label: 'PY', maxSize: CODE_FILE_MAX },
  'text/x-java': { icon: FileCodeIcon, label: 'JAVA', maxSize: CODE_FILE_MAX },
  'text/x-c': { icon: FileCodeIcon, label: 'C', maxSize: CODE_FILE_MAX },
  'text/x-c++': { icon: FileCodeIcon, label: 'CPP', maxSize: CODE_FILE_MAX },
  'text/x-go': { icon: FileCodeIcon, label: 'GO', maxSize: CODE_FILE_MAX },
  'text/x-rust': { icon: FileCodeIcon, label: 'RS', maxSize: CODE_FILE_MAX },
  'text/x-ruby': { icon: FileCodeIcon, label: 'RB', maxSize: CODE_FILE_MAX },
  'text/x-php': { icon: FileCodeIcon, label: 'PHP', maxSize: CODE_FILE_MAX },
  'text/x-swift': { icon: FileCodeIcon, label: 'SWIFT', maxSize: CODE_FILE_MAX },
  'text/x-kotlin': { icon: FileCodeIcon, label: 'KT', maxSize: CODE_FILE_MAX },
  'text/x-scala': { icon: FileCodeIcon, label: 'SCALA', maxSize: CODE_FILE_MAX },
  'text/x-sql': { icon: FileCodeIcon, label: 'SQL', maxSize: CODE_FILE_MAX },
  'application/sql': { icon: FileCodeIcon, label: 'SQL', maxSize: CODE_FILE_MAX },
  'text/x-sh': { icon: FileCodeIcon, label: 'SH', maxSize: CODE_FILE_MAX },
  'application/x-sh': { icon: FileCodeIcon, label: 'SH', maxSize: CODE_FILE_MAX },

  // Config/Data files
  'application/json': { icon: FileCodeIcon, label: 'JSON', maxSize: CONFIG_FILE_MAX },
  'text/json': { icon: FileCodeIcon, label: 'JSON', maxSize: CONFIG_FILE_MAX },
  'text/yaml': { icon: FileCodeIcon, label: 'YAML', maxSize: CONFIG_FILE_MAX },
  'text/x-yaml': { icon: FileCodeIcon, label: 'YAML', maxSize: CONFIG_FILE_MAX },
  'application/x-yaml': { icon: FileCodeIcon, label: 'YAML', maxSize: CONFIG_FILE_MAX },
  'text/xml': { icon: FileCodeIcon, label: 'XML', maxSize: CONFIG_FILE_MAX },
  'application/xml': { icon: FileCodeIcon, label: 'XML', maxSize: CONFIG_FILE_MAX },
  'text/markdown': { icon: FileTextIcon, label: 'MD', maxSize: TEXT_FILE_MAX },
  'text/x-markdown': { icon: FileTextIcon, label: 'MD', maxSize: TEXT_FILE_MAX },
};

// Extension-based fallback for when MIME type detection fails
const EXTENSION_FALLBACKS = {
  '.js': { icon: FileCodeIcon, label: 'JS', maxSize: CODE_FILE_MAX, type: 'text/javascript' },
  '.jsx': { icon: FileCodeIcon, label: 'JSX', maxSize: CODE_FILE_MAX, type: 'text/javascript' },
  '.ts': { icon: FileCodeIcon, label: 'TS', maxSize: CODE_FILE_MAX, type: 'text/typescript' },
  '.tsx': { icon: FileCodeIcon, label: 'TSX', maxSize: CODE_FILE_MAX, type: 'text/typescript' },
  '.py': { icon: FileCodeIcon, label: 'PY', maxSize: CODE_FILE_MAX, type: 'text/x-python' },
  '.java': { icon: FileCodeIcon, label: 'JAVA', maxSize: CODE_FILE_MAX, type: 'text/x-java' },
  '.c': { icon: FileCodeIcon, label: 'C', maxSize: CODE_FILE_MAX, type: 'text/x-c' },
  '.cpp': { icon: FileCodeIcon, label: 'CPP', maxSize: CODE_FILE_MAX, type: 'text/x-c++' },
  '.cc': { icon: FileCodeIcon, label: 'CPP', maxSize: CODE_FILE_MAX, type: 'text/x-c++' },
  '.h': { icon: FileCodeIcon, label: 'H', maxSize: CODE_FILE_MAX, type: 'text/x-c' },
  '.hpp': { icon: FileCodeIcon, label: 'HPP', maxSize: CODE_FILE_MAX, type: 'text/x-c++' },
  '.go': { icon: FileCodeIcon, label: 'GO', maxSize: CODE_FILE_MAX, type: 'text/x-go' },
  '.rs': { icon: FileCodeIcon, label: 'RS', maxSize: CODE_FILE_MAX, type: 'text/x-rust' },
  '.rb': { icon: FileCodeIcon, label: 'RB', maxSize: CODE_FILE_MAX, type: 'text/x-ruby' },
  '.php': { icon: FileCodeIcon, label: 'PHP', maxSize: CODE_FILE_MAX, type: 'text/x-php' },
  '.swift': { icon: FileCodeIcon, label: 'SWIFT', maxSize: CODE_FILE_MAX, type: 'text/x-swift' },
  '.kt': { icon: FileCodeIcon, label: 'KT', maxSize: CODE_FILE_MAX, type: 'text/x-kotlin' },
  '.scala': { icon: FileCodeIcon, label: 'SCALA', maxSize: CODE_FILE_MAX, type: 'text/x-scala' },
  '.sql': { icon: FileCodeIcon, label: 'SQL', maxSize: CODE_FILE_MAX, type: 'text/x-sql' },
  '.sh': { icon: FileCodeIcon, label: 'SH', maxSize: CODE_FILE_MAX, type: 'text/x-sh' },
  '.bash': { icon: FileCodeIcon, label: 'SH', maxSize: CODE_FILE_MAX, type: 'text/x-sh' },
  '.html': { icon: FileCodeIcon, label: 'HTML', maxSize: CODE_FILE_MAX, type: 'text/html' },
  '.htm': { icon: FileCodeIcon, label: 'HTML', maxSize: CODE_FILE_MAX, type: 'text/html' },
  '.css': { icon: FileCodeIcon, label: 'CSS', maxSize: CODE_FILE_MAX, type: 'text/css' },
  '.scss': { icon: FileCodeIcon, label: 'SCSS', maxSize: CODE_FILE_MAX, type: 'text/css' },
  '.sass': { icon: FileCodeIcon, label: 'SASS', maxSize: CODE_FILE_MAX, type: 'text/css' },
  '.less': { icon: FileCodeIcon, label: 'LESS', maxSize: CODE_FILE_MAX, type: 'text/css' },
  '.json': { icon: FileCodeIcon, label: 'JSON', maxSize: CONFIG_FILE_MAX, type: 'application/json' },
  '.yaml': { icon: FileCodeIcon, label: 'YAML', maxSize: CONFIG_FILE_MAX, type: 'text/yaml' },
  '.yml': { icon: FileCodeIcon, label: 'YAML', maxSize: CONFIG_FILE_MAX, type: 'text/yaml' },
  '.xml': { icon: FileCodeIcon, label: 'XML', maxSize: CONFIG_FILE_MAX, type: 'application/xml' },
  '.md': { icon: FileTextIcon, label: 'MD', maxSize: TEXT_FILE_MAX, type: 'text/markdown' },
  '.markdown': { icon: FileTextIcon, label: 'MD', maxSize: TEXT_FILE_MAX, type: 'text/markdown' },
  '.txt': { icon: FileTextIcon, label: 'TXT', maxSize: TEXT_FILE_MAX, type: 'text/plain' },
  '.csv': { icon: FileSpreadsheetIcon, label: 'CSV', maxSize: TEXT_FILE_MAX, type: 'text/csv' },
  '.log': { icon: FileTextIcon, label: 'LOG', maxSize: TEXT_FILE_MAX, type: 'text/plain' },
  '.env.example': { icon: FileTextIcon, label: 'ENV', maxSize: CODE_FILE_MAX, type: 'text/plain' },
};

const MAX_FILES = 10;
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.csv,.txt,.js,.jsx,.ts,.tsx,.py,.java,.c,.cpp,.cc,.h,.hpp,.go,.rs,.rb,.php,.swift,.kt,.scala,.sql,.sh,.bash,.html,.htm,.css,.scss,.sass,.less,.json,.yaml,.yml,.xml,.md,.markdown,.log';

// Helper to get file config by extension when MIME type is unreliable
const getFileConfigByExtension = (filename) => {
  const ext = filename.toLowerCase().match(/\.[^.]+$/)?.[0];
  return ext ? EXTENSION_FALLBACKS[ext] : null;
};

const FileUpload = ({ files, onFilesChange, disabled }) => {
  const fileInputRef = useRef(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const validateFile = (file) => {
    // Check if file type is supported (try MIME type first, then extension fallback)
    let fileConfig = SUPPORTED_FILES[file.type];

    // If MIME type not recognized, try extension-based detection
    if (!fileConfig) {
      const extConfig = getFileConfigByExtension(file.name);
      if (extConfig) {
        fileConfig = extConfig;
      }
    }

    if (!fileConfig) {
      return `Unsupported file type: ${file.type || 'unknown'}. Supported: Images, PDFs, code files (JS, TS, PY, etc.), and config files (JSON, YAML, etc.)`;
    }

    // Check file size
    if (file.size > fileConfig.maxSize) {
      const maxSizeKB = fileConfig.maxSize / 1024;
      const fileSizeKB = Math.round(file.size / 1024);
      if (maxSizeKB >= 1024) {
        return `${file.name} is too large (${fileSizeKB}KB). Max for ${fileConfig.label}: ${(maxSizeKB / 1024).toFixed(0)}MB`;
      }
      return `${file.name} is too large (${fileSizeKB}KB). Max for ${fileConfig.label}: ${maxSizeKB}KB to stay within token limits`;
    }

    return null;
  };

  const processFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data URL prefix

        // Determine the correct MIME type (use extension fallback if needed)
        let fileType = file.type;
        if (!SUPPORTED_FILES[fileType]) {
          const extConfig = getFileConfigByExtension(file.name);
          if (extConfig) {
            fileType = extConfig.type;
          }
        }

        resolve({
          id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          type: fileType,
          size: file.size,
          data: base64,
          preview: file.type.startsWith('image/') ? reader.result : null
        });
      };

      reader.onerror = () => {
        reject(new Error(`Failed to read file: ${file.name}`));
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (selectedFiles) => {
    setError('');

    // Check max files limit
    const totalFiles = files.length + selectedFiles.length;
    if (totalFiles > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed. You have ${files.length}, trying to add ${selectedFiles.length}.`);
      return;
    }

    const newFiles = [];
    const errors = [];

    for (const file of selectedFiles) {
      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
        continue;
      }

      // Check for duplicates
      const isDuplicate = files.some(f => f.name === file.name && f.size === file.size);
      if (isDuplicate) {
        errors.push(`${file.name} is already attached`);
        continue;
      }

      try {
        const processedFile = await processFile(file);
        newFiles.push(processedFile);
      } catch (err) {
        errors.push(err.message);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('. '));
    }

    if (newFiles.length > 0) {
      onFilesChange([...files, ...newFiles]);
    }
  };

  const handleInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      handleFileSelect(selectedFiles);
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  const handleRemoveFile = (fileId) => {
    onFilesChange(files.filter(f => f.id !== fileId));
    setError('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles);
    }
  };

  const getFileIcon = (fileType, fileName) => {
    // Try MIME type first
    let config = SUPPORTED_FILES[fileType];

    // Fallback to extension-based detection
    if (!config && fileName) {
      config = getFileConfigByExtension(fileName);
    }

    if (config) {
      const IconComponent = config.icon;
      return <IconComponent className="h-4 w-4" />;
    }
    return <FileTextIcon className="h-4 w-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-3">
      {/* Upload button and drop zone */}
      <div
        className={`flex items-center gap-3 ${isDragging ? 'opacity-50' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXTENSIONS}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || files.length >= MAX_FILES}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all font-medium text-sm ${
            disabled || files.length >= MAX_FILES
              ? 'bg-slate-800/50 border-slate-700 text-slate-500 cursor-not-allowed'
              : 'bg-slate-800/70 border-slate-600 text-slate-300 hover:bg-slate-700/70 hover:border-cyan-500/50 hover:text-cyan-300'
          }`}
          title={files.length >= MAX_FILES ? `Maximum ${MAX_FILES} files reached` : 'Attach files (images, code, PDFs, configs)'}
        >
          <PlusIcon className="h-4 w-4" />
          <span>Attach</span>
          {files.length > 0 && (
            <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-300 text-xs rounded-md">
              {files.length}/{MAX_FILES}
            </span>
          )}
        </button>

        {files.length === 0 && (
          <span className="text-slate-500 text-sm">
            Drop files here or click to attach (images, code, PDFs, configs)
          </span>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-600/50 rounded-xl">
          <AlertCircleIcon className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* File previews */}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="group relative flex items-center gap-2 px-3 py-2 bg-slate-800/70 border border-slate-600/50 rounded-xl hover:border-slate-500/50 transition-all"
            >
              {/* Image preview or icon */}
              {file.preview ? (
                <div className="h-8 w-8 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-8 w-8 rounded-lg bg-slate-700 flex items-center justify-center text-slate-400 flex-shrink-0">
                  {getFileIcon(file.type, file.name)}
                </div>
              )}

              {/* File info */}
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-slate-200 truncate max-w-[120px]" title={file.name}>
                  {file.name}
                </span>
                <span className="text-xs text-slate-500">
                  {formatFileSize(file.size)}
                </span>
              </div>

              {/* Remove button */}
              <button
                type="button"
                onClick={() => handleRemoveFile(file.id)}
                disabled={disabled}
                className="ml-1 p-1 text-slate-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                title="Remove file"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 flex items-center justify-center pointer-events-none">
          <div className="p-8 border-2 border-dashed border-cyan-500 rounded-2xl bg-slate-800/90">
            <p className="text-cyan-300 text-lg font-medium">Drop files here</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
